export default defineNuxtPlugin((nuxtApp) => {
  const runtimeConfig = useRuntimeConfig()
  const { token, refreshToken, refresh } = useAuth()
  const { data, rawRefreshToken, clearToken } = useAuthState()
  // Shared so a burst of requests failing on the same expired token triggers one refresh.
  let refreshing: Promise<unknown> | null = null

  const api = $fetch.create({
    baseURL: runtimeConfig.public.baseURL,
    onRequest({ options }) {
      // ofetch has already normalised headers into a Headers instance here (and on retries);
      // spreading it would drop every other header.
      const headers = new Headers(options.headers as HeadersInit)
      if (token.value) headers.set('Authorization', token.value)
      else headers.delete('Authorization')  // retry after a failed refresh goes out anonymously
      options.headers = headers
    },
    // The access token lives 15 min and nuxt-auth only refreshes it when the tab becomes
    // visible again, so a page left open (e.g. while editing a file in another window)
    // starts getting 403 token_not_valid — on every endpoint, public ones included, since
    // DRF rejects an invalid token even where auth isn't required. Refresh once and retry.
    async onResponseError({ response, options }) {
      const opts = options as typeof options & { _authRetried?: boolean }
      if (opts._authRetried || response._data?.code !== 'token_not_valid') return
      if (refreshToken.value) {
        refreshing ??= refresh().finally(() => { refreshing = null })
        try {
          await refreshing
        } catch {}
      }
      if (!token.value || opts.headers?.get?.('Authorization') === token.value) {
        // Refresh token expired too. Drop the dead session so the header shows Login again
        // (it keys off cached session data, not token validity) and retry anonymously.
        data.value = null
        clearToken()
        rawRefreshToken.value = null
      }
      opts._authRetried = true
      opts.retry = 1
      opts.retryStatusCodes = [response.status]
      opts.retryDelay = 0
    },
  })

  // Expose to useNuxtApp().$api
  return {
    provide: {
      api
    }
  }
})
